import React from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';

import {useCustomerMgr, useLocalesMgr, useLocalStorage} from 'coreHooks';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Button from 'brandComponents/Button';
import ShowOnDesktop from 'brandComponents/Responsive/ShowOnDesktop';
import ShowOnMobile from 'brandComponents/Responsive/ShowOnMobile';
import SocialLoginFactory from 'brandComponents/SocialLogin/SocialLoginFactory';
import Typography from 'brandComponents/Typography';

import {createLoginFlow, schema} from './helpers';
import {IAuthLoginFormProps} from './IAuthLoginForm';
import messages from './messages';
import {
	AuthLoginFormCredentials,
	AuthLoginFormError,
	AuthLoginFormForgotPassword,
	AuthLoginFormRememberMe,
} from './Subcomponents';

import styles from './AuthLoginForm.module.scss';

const AuthLoginForm = (props: IAuthLoginFormProps) => {
	const {classNames, children, onSuccess} = props;
	const CustomerMgr = useCustomerMgr();
	const localesMgr = useLocalesMgr();
	const {managerRepository} = useGeneralProvider();
	const intl = useIntl();
	const [, setLocalStorageChecked] = useLocalStorage<boolean>('login-flyout_remember-me', false);
	const [, setLocalStorageEmail] = useLocalStorage<string>('login-flyout_email', '');

	const formatEntry = (entry) => [entry[0], intl.formatMessage(entry[1])];

	const currentCountry = localesMgr.getCurrentCountry();
	const currentCountrySocialLoginTypes = currentCountry.getSocialLoginButtons();
	const brandSocialLoginTypes = managerRepository
		.getConfigurationsMgr()
		?.get('sitePreferences.socialLoginButtons')
		?.split(',');
	const socialLoginTypes = currentCountrySocialLoginTypes || brandSocialLoginTypes;

	const onValidSubmit = async (values) => {
		const errors = schema.validate(values);

		if (Object.keys(errors).length > 0) {
			return Object.fromEntries(Object.entries(errors).map(formatEntry));
		}

		const loginFlow = createLoginFlow(CustomerMgr);

		return loginFlow(values).then((failure) => {
			if (!failure) {
				if (values.rememberMe) {
					setLocalStorageEmail(values.email);
				}

				setLocalStorageChecked(!!values.rememberMe);

				onSuccess && onSuccess();
			}

			return failure;
		});
	};

	return (
		<Form
			onSubmit={onValidSubmit}
			render={({handleSubmit}) => {
				return (
					<form noValidate onSubmit={handleSubmit} className={classNames}>
						<Typography variant="h2" classNames={styles['auth__login-form__title']}>
							{intl.formatMessage(messages.title)}
						</Typography>

						<ShowOnDesktop>
							<AuthLoginFormError classNames={styles['auth__login-form__error']} />
						</ShowOnDesktop>

						<AuthLoginFormCredentials schema={schema} />

						<div className={styles['auth__login-form__extensions']}>
							<AuthLoginFormForgotPassword classNames={styles['auth__login-form__forgot-password']} />
							<AuthLoginFormRememberMe />
						</div>

						{children}

						<ShowOnMobile>
							<AuthLoginFormError classNames={styles['auth__login-form__error']} />
						</ShowOnMobile>

						<div className={styles['auth__login-form__buttons']}>
							<Button type="submit">{intl.formatMessage(messages.submit)}</Button>

							{socialLoginTypes?.map((type) => {
								return <SocialLoginFactory key={type} type={type} />;
							})}
						</div>
					</form>
				);
			}}
		/>
	);
};

export default AuthLoginForm;
